<template>
  <div class="findingo-score-chart">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { markRaw } from 'vue'

/* global Chart */

function clampScore(value) {
  const normalized = String(value ?? '').replace(',', '.')
  const n = Number(normalized)

  if (!Number.isFinite(n)) return 0

  return Math.max(0, Math.min(10, Number(n.toFixed(1))))
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, height / 2, width / 2)

  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + r)
  ctx.lineTo(x + width, y + height - r)
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
  ctx.lineTo(x + r, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function drawFryingIcon(ctx, x, y, size, color) {
  ctx.save()

  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = 1.8
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  // Pommes / Frittiergut
  const fries = [
    { dx: -0.42, top: -0.9, bottom: -0.18, tilt: -0.1 },
    { dx: 0, top: -1.0, bottom: -0.18, tilt: 0.04 },
    { dx: 0.42, top: -0.86, bottom: -0.18, tilt: 0.12 }
  ]

  fries.forEach((fry) => {
    ctx.beginPath()
    ctx.moveTo(x + size * fry.dx, y + size * fry.bottom)
    ctx.lineTo(x + size * (fry.dx + fry.tilt), y + size * fry.top)
    ctx.stroke()
  })

  // Korb
  ctx.beginPath()
  ctx.moveTo(x - size * 0.72, y - size * 0.18)
  ctx.lineTo(x + size * 0.72, y - size * 0.18)
  ctx.lineTo(x + size * 0.52, y + size * 0.62)
  ctx.lineTo(x - size * 0.52, y + size * 0.62)
  ctx.closePath()
  ctx.stroke()

  // Korblinien
  ctx.lineWidth = 1.3

  ctx.beginPath()
  ctx.moveTo(x - size * 0.34, y - size * 0.08)
  ctx.lineTo(x - size * 0.24, y + size * 0.5)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(x, y - size * 0.08)
  ctx.lineTo(x, y + size * 0.52)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(x + size * 0.34, y - size * 0.08)
  ctx.lineTo(x + size * 0.24, y + size * 0.5)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(x - size * 0.52, y + size * 0.22)
  ctx.lineTo(x + size * 0.52, y + size * 0.22)
  ctx.stroke()

  // kleiner Dampf-/Hitzeakzent
  ctx.lineWidth = 1.4

  ctx.beginPath()
  ctx.moveTo(x - size * 0.82, y - size * 0.52)
  ctx.quadraticCurveTo(
    x - size * 1.02,
    y - size * 0.72,
    x - size * 0.78,
    y - size * 0.9
  )
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(x + size * 0.82, y - size * 0.52)
  ctx.quadraticCurveTo(
    x + size * 1.02,
    y - size * 0.72,
    x + size * 0.78,
    y - size * 0.9
  )
  ctx.stroke()

  ctx.restore()
}

function drawVersatilityIcon(ctx, x, y, size, color) {
  ctx.save()

  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = 1.7
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const points = [
    { x: x, y: y - size * 0.72 },
    { x: x + size * 0.68, y: y - size * 0.08 },
    { x: x + size * 0.36, y: y + size * 0.68 },
    { x: x - size * 0.36, y: y + size * 0.68 },
    { x: x - size * 0.68, y: y - size * 0.08 }
  ]

  // Verbindungen als Symbol für mehrere Funktionen
  ctx.beginPath()
  points.forEach((point, index) => {
    if (index === 0) {
      ctx.moveTo(point.x, point.y)
    } else {
      ctx.lineTo(point.x, point.y)
    }
  })
  ctx.closePath()
  ctx.stroke()

  // Zentrum
  ctx.beginPath()
  ctx.arc(x, y, size * 0.2, 0, Math.PI * 2)
  ctx.fill()

  // Linien zum Zentrum
  points.forEach((point) => {
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
  })

  // äußere Punkte
  points.forEach((point) => {
    ctx.beginPath()
    ctx.arc(point.x, point.y, size * 0.16, 0, Math.PI * 2)
    ctx.fill()
  })

  // kleiner Plus-Akzent
  ctx.lineWidth = 1.5

  ctx.beginPath()
  ctx.moveTo(x + size * 0.58, y + size * 0.48)
  ctx.lineTo(x + size * 0.88, y + size * 0.48)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(x + size * 0.73, y + size * 0.33)
  ctx.lineTo(x + size * 0.73, y + size * 0.63)
  ctx.stroke()

  ctx.restore()
}

function drawScoreIcon(ctx, type, x, y, color) {
  if (type === 'frying') {
    drawFryingIcon(ctx, x, y, 9.8, color)
  } else if (type === 'versatility') {
    drawVersatilityIcon(ctx, x, y, 9.2, color)
  }
}

export default {
  name: 'ProductChart',

  props: {
    product: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      chart: null,
      rafId: null,
      resizeRafId: null,
      retryTimer: null,
      resizeObserver: null,
      isUnmounted: false,
      lastKnownWidth: 0,
      instanceKey: Math.random().toString(36).slice(2)
    }
  },

  computed: {
    chartKey() {
      const p = this.product || {}

      return [
        p.id,
        p.frittierung_pred,
        p.vielseitigkeit_pred
      ].join('|')
    }
  },

  mounted() {
    this.isUnmounted = false

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isUnmounted) return

        const wrapper = this.getWrapper()
        if (!wrapper) return

        const width = wrapper.getBoundingClientRect().width

        if (width < 40) return

        if (!this.chart) {
          this.scheduleCreateChart(40)
          return
        }

        this.queueChartResize()
      })

      this.resizeObserver.observe(this.$el)

      if (this.$el.parentElement) {
        this.resizeObserver.observe(this.$el.parentElement)
      }
    }

    this.scheduleCreateChart(500)
  },

  beforeUnmount() {
    this.cleanupChart()
  },

  watch: {
    chartKey() {
      this.scheduleCreateChart(120)
    }
  },

  methods: {
    getWrapper() {
      const canvas = this.$refs.chartCanvas
      return canvas ? canvas.parentElement : null
    },

    getTooltipEl() {
      const wrapper = this.getWrapper()
      if (!wrapper) return null

      let tooltipEl = wrapper.querySelector('.findingo-score-tooltip')

      if (!tooltipEl) {
        tooltipEl = document.createElement('div')
        tooltipEl.className = 'findingo-score-tooltip'
        wrapper.appendChild(tooltipEl)
      }

      return tooltipEl
    },

    hideExternalTooltip() {
      const wrapper = this.getWrapper()
      const tooltipEl = wrapper
        ? wrapper.querySelector('.findingo-score-tooltip')
        : null

      if (!tooltipEl) return

      tooltipEl.style.opacity = '0'
      tooltipEl.style.transform = 'translate(-50%, calc(-100% - 6px)) scale(0.96)'
    },

    removeExternalTooltip() {
      const wrapper = this.getWrapper()
      const tooltipEl = wrapper
        ? wrapper.querySelector('.findingo-score-tooltip')
        : null

      if (tooltipEl) {
        tooltipEl.remove()
      }
    },

    renderExternalTooltip(context) {
      const { chart, tooltip } = context
      const tooltipEl = this.getTooltipEl()
      const wrapper = this.getWrapper()

      if (!tooltipEl || !wrapper || !tooltip || tooltip.opacity === 0) {
        this.hideExternalTooltip()
        return
      }

      const dataPoint = tooltip.dataPoints && tooltip.dataPoints[0]

      if (!dataPoint) {
        this.hideExternalTooltip()
        return
      }

      const value = Number(dataPoint.raw || 0)
      const canvasRect = chart.canvas.getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()
      const left = canvasRect.left - wrapperRect.left + tooltip.caretX
      const top = canvasRect.top - wrapperRect.top + tooltip.caretY

      tooltipEl.textContent = `${value.toFixed(1)}/10`
      tooltipEl.style.opacity = '1'
      tooltipEl.style.left = `${left}px`
      tooltipEl.style.top = `${top}px`
      tooltipEl.style.transform = 'translate(-50%, calc(-100% - 8px)) scale(1)'
    },

    cleanupChart() {
      this.isUnmounted = true

      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }

      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId)
        this.resizeRafId = null
      }

      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }

      this.destroyChart()
      this.removeExternalTooltip()
    },

    destroyChart() {
      const canvas = this.$refs.chartCanvas
      let existingChart = null

      if (canvas && typeof Chart !== 'undefined' && typeof Chart.getChart === 'function') {
        existingChart = Chart.getChart(canvas)

        if (existingChart) {
          existingChart.destroy()
        }
      }

      if (this.chart && this.chart !== existingChart) {
        this.chart.destroy()
      }

      this.chart = null
      this.hideExternalTooltip()
    },

    queueChartResize() {
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId)
        this.resizeRafId = null
      }

      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null

        if (!this.chart) {
          this.scheduleCreateChart(0)
          return
        }

        this.chart.resize()
        this.chart.update('none')
      })
    },

    scheduleCreateChart(delay = 0) {
      if (this.isUnmounted) return

      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }

      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }

      this.retryTimer = setTimeout(() => {
        this.retryTimer = null
        this.waitForStableLayoutAndCreate(0)
      }, delay)
    },

    waitForStableLayoutAndCreate(attempt) {
      if (this.isUnmounted) return

      this.$nextTick(() => {
        this.rafId = requestAnimationFrame(() => {
          this.rafId = requestAnimationFrame(() => {
            this.rafId = null

            const canvas = this.$refs.chartCanvas
            const wrapper = this.getWrapper()

            if (!canvas || !wrapper) return

            const rect = wrapper.getBoundingClientRect()
            const width = rect.width
            const height = rect.height

            if (width < 40 || height < 40) {
              if (attempt < 20) {
                this.retryTimer = setTimeout(() => {
                  this.retryTimer = null
                  this.waitForStableLayoutAndCreate(attempt + 1)
                }, 80)
              }

              return
            }

            const widthChanged = Math.abs(width - this.lastKnownWidth) > 1
            this.lastKnownWidth = width

            if (widthChanged && attempt < 4) {
              this.retryTimer = setTimeout(() => {
                this.retryTimer = null
                this.waitForStableLayoutAndCreate(attempt + 1)
              }, 60)

              return
            }

            this.createChart()
          })
        })
      })
    },

    createChart() {
      this.destroyChart()

      const canvas = this.$refs.chartCanvas
      if (!canvas) return

      if (typeof Chart === 'undefined') {
        console.error('Chart.js ist nicht verfügbar. Lade Chart.js global oder importiere chart.js/auto.')
        return
      }

      const ctx = canvas.getContext('2d')
      const p = this.product || {}

      const values = [
        clampScore(p.frittierung_pred),
        clampScore(p.vielseitigkeit_pred)
      ]

      const rows = [
        {
          label: 'Frittieren',
          iconType: 'frying',
          iconBg: 'rgba(188, 93, 76, 0.13)',
          iconColor: '#a65a45',
          fillStart: '#f8ddd8',
          fillEnd: '#edada4',
          border: 'rgba(184, 92, 74, 0.28)'
        },
        {
          label: 'Vielseitigkeit',
          iconType: 'versatility',
          iconBg: 'rgba(91, 130, 205, 0.13)',
          iconColor: '#4d6fb2',
          fillStart: '#e3ecff',
          fillEnd: '#b8c9f4',
          border: 'rgba(80, 116, 190, 0.28)'
        }
      ]

      const labelFont = '700 14.5px Inter, "Open Sans", Arial, sans-serif'

      ctx.save()
      ctx.font = labelFont

      const maxLabelTextWidth = Math.max(
        ...rows.map((row) => ctx.measureText(row.label).width)
      )

      ctx.restore()

      const minimumLabelAreaWidth = 122
      const iconSize = 32
      const iconToTextGap = 18
      const leftOuterPadding = 0
      const rightSafetyGap = 16

      const labelAreaWidth = Math.max(
        minimumLabelAreaWidth,
        Math.ceil(
          leftOuterPadding +
          iconSize +
          iconToTextGap +
          maxLabelTextWidth +
          rightSafetyGap
        )
      )

      const findingoScoreBars = {
        id: `findingoScoreBars-${this.instanceKey}`,

        beforeDatasetsDraw(chart) {
          const { ctx, chartArea, scales } = chart
          const meta = chart.getDatasetMeta(0)

          if (!chartArea || !meta || !meta.data || !meta.data.length) return

          ctx.save()

          const trackHeight = 14
          const left = scales.x.getPixelForValue(0)
          const right = scales.x.getPixelForValue(10)
          const width = right - left

          meta.data.forEach((bar) => {
            const y = bar.y - trackHeight / 2

            ctx.fillStyle = 'rgba(255, 255, 255, 0.84)'
            ctx.strokeStyle = 'rgba(24, 34, 49, 0.13)'
            ctx.lineWidth = 1

            drawRoundedRect(ctx, left, y, width, trackHeight, 999)
            ctx.fill()
            ctx.stroke()
          })

          ctx.restore()
        },

        afterDatasetsDraw(chart) {
          const { ctx, chartArea } = chart
          const meta = chart.getDatasetMeta(0)

          if (!chartArea || !meta || !meta.data || !meta.data.length) return

          ctx.save()

          rows.forEach((row, index) => {
            const bar = meta.data[index]
            if (!bar) return

            const y = bar.y
            const iconX = chartArea.left - labelAreaWidth + 16
            const labelX = chartArea.left - labelAreaWidth + 50

            ctx.beginPath()
            ctx.arc(iconX, y, 16, 0, Math.PI * 2)
            ctx.fillStyle = row.iconBg
            ctx.fill()

            drawScoreIcon(ctx, row.iconType, iconX, y, row.iconColor)

            ctx.font = labelFont
            ctx.fillStyle = '#182231'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(row.label, labelX, y)
          })

          ctx.restore()
        }
      }

      const chartConfig = {
        type: 'bar',

        data: {
          labels: ['Frittieren', 'Vielseitigkeit'],
          datasets: [
            {
              data: values,

              backgroundColor: (context) => {
                const chart = context.chart
                const { ctx, chartArea, scales } = chart
                const row = rows[context.dataIndex]

                if (!chartArea || !row) {
                  return '#e5e7eb'
                }

                const gradient = ctx.createLinearGradient(
                  scales.x.getPixelForValue(0),
                  0,
                  scales.x.getPixelForValue(10),
                  0
                )

                gradient.addColorStop(0, row.fillStart)
                gradient.addColorStop(1, row.fillEnd)

                return gradient
              },

              borderColor: (context) => {
                const row = rows[context.dataIndex]
                return row ? row.border : 'rgba(0,0,0,0.12)'
              },

              borderWidth: 1,
              borderRadius: 999,
              borderSkipped: false,
              barThickness: 14,
              maxBarThickness: 14,
              categoryPercentage: 0.62,
              barPercentage: 1,
              clip: false
            }
          ]
        },

        plugins: [findingoScoreBars],

        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',

          events: [
            'mousemove',
            'mouseout',
            'click',
            'touchstart',
            'touchmove'
          ],

          interaction: {
            mode: 'nearest',
            axis: 'y',
            intersect: false
          },

          animation: {
            duration: 650,
            easing: 'easeOutQuart'
          },

          layout: {
            padding: {
              left: labelAreaWidth,
              top: 12,
              bottom: 10
            }
          },

          plugins: {
            legend: {
              display: false
            },

            tooltip: {
              enabled: false,
              external: this.renderExternalTooltip
            }
          },

          scales: {
            x: {
              display: false,
              min: 0,
              max: 10,
              grid: {
                display: false,
                drawBorder: false
              },
              ticks: {
                display: false
              }
            },

            y: {
              display: false,
              grid: {
                display: false,
                drawBorder: false
              },
              ticks: {
                display: false
              }
            }
          }
        }
      }

      this.chart = markRaw(new Chart(ctx, chartConfig))

      this.$nextTick(() => {
        this.queueChartResize()
      })
    }
  }
}
</script>

<style scoped>
.findingo-score-chart {
  position: relative;
  width: 100%;
  height: 104px;
  min-height: 104px;
  overflow: visible;
}

.findingo-score-chart canvas {
  display: block;
  width: 100% !important;
  height: 104px !important;
}

.findingo-score-chart :deep(.findingo-score-tooltip) {
  position: absolute;
  z-index: 30;
  top: 0;
  left: 0;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: fit-content;
  min-width: 0;
  height: auto;
  min-height: 0;
  padding: 4px 7px;
  margin: 0;
  color: #ffffff;
  background: rgba(17, 24, 39, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.16);
  font-family: Inter, "Open Sans", Arial, sans-serif;
  font-size: 10.5px;
  font-weight: 750;
  line-height: 1;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 6px)) scale(0.96);
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}
</style>